import {useCallback, useEffect, useState} from 'react';
import useScreenSize from '../../hooks/useScreenSize';
import {createMilestone, deleteMilestone, findAllMilestones, updateMilestone} from '../../api/milestone';
import Loader from '../../components/common/Loader';
import TransactionFilter from '../../components/common/TransactionFilter/TransactionFilter';
import TransactionFilterButton from '../../components/common/TransactionFilter/TransactionFilterButton';
import MilestoneChart from './components/MilestoneChart';
import './MilestonesPage.scss';
import {useSelector} from 'react-redux';
import {selectAccountsSorted} from '../../store/selectors';

const emptyForm = () => ({id: null, title: '', description: '', milestoneDate: ''});

const MILESTONE_FILTER_ENABLED = {accounts: false, types: false, projects: false};

const MilestonesPage = () => {
    const accounts = useSelector(selectAccountsSorted);
    const [milestones, setMilestones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({searchText: '', from: null, to: null});
    const {isSidebarOpen} = useScreenSize();
    const [filterOpen, setFilterOpen] = useState(isSidebarOpen);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);
    const [hoveredId, setHoveredId] = useState(null);
    const [reloadTick, setReloadTick] = useState(0);
    const reload = useCallback(() => setReloadTick(t => t + 1), []);

    const onFiltersChange = useCallback(f => {
        setFilters(prev => {
            if (prev.searchText === f.searchText && prev.from === f.from && prev.to === f.to) return prev;
            return {searchText: f.searchText, from: f.from, to: f.to};
        });
    }, []);

    useEffect(() => {
        setLoading(true);
        findAllMilestones(filters)
            .then(data => setMilestones(data))
            .finally(() => setLoading(false));
    }, [filters, reloadTick]); // eslint-disable-line

    const openModal = () => {
        setForm(emptyForm());
        setShowModal(true);
    };

    const openEditModal = (m) => {
        setForm({id: m.id, title: m.title, description: m.description || '', milestoneDate: m.milestoneDate});
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setForm(emptyForm());
    };

    const save = () => {
        setSaving(true);
        const body = {title: form.title, description: form.description || null, milestoneDate: form.milestoneDate};
        const request = form.id ? updateMilestone(form.id, body) : createMilestone(body);
        request
            .then(() => { closeModal(); reload(); })
            .finally(() => setSaving(false));
    };

    const remove = (id) => {
        if (!window.confirm('Delete this milestone?')) return;
        deleteMilestone(id).then(reload);
    };

    const canSubmit = form.title.trim() && form.milestoneDate;

    return (
        <div className="capital-page milestones-page">
            <div className="capital-page__header">
                <TransactionFilterButton onClick={() => setFilterOpen(v => !v)}/>
                <h2 className="capital-page__title">Milestones</h2>
                <button className="wallet-add-button" onClick={openModal}>
                    + Milestone
                </button>
            </div>

            <div className="capital-page__body">
                <div className="milestones-layout">
                    <TransactionFilter
                        onFiltersChange={onFiltersChange}
                        initialValues={{from: null, to: null}}
                        initialPreset='all-time'
                        enabled={MILESTONE_FILTER_ENABLED}
                        expanded={filterOpen}
                        accounts={accounts}
                        onExpandedChange={setFilterOpen}
                    />

                    <div className="milestones-content">
                        {loading ? <Loader/> : milestones.length === 0 ? (
                            <p className="capital-page__placeholder">No milestones yet.</p>
                        ) : (
                            <>
                                <MilestoneChart
                                    milestones={milestones}
                                    hoveredId={hoveredId}
                                    onHover={setHoveredId}
                                />
                                <div className="milestones-timeline">
                                    {milestones.map(m => (
                                        <div
                                            key={m.id}
                                            onClick={() => openEditModal(m)}
                                            className={`milestone-item${hoveredId === m.id ? ' milestone-item--highlighted' : ''}`}
                                            onMouseEnter={() => setHoveredId(m.id)}
                                            onMouseLeave={() => setHoveredId(null)}
                                        >
                                            <div className="milestone-item__dot"/>
                                            <div className="milestone-item__content" >
                                                <div className="milestone-item__header">
                                                    <span className="milestone-item__date">{m.milestoneDate}</span>
                                                    <span className="milestone-item__title">{m.title}</span>
                                                    <button
                                                        className="btn-del"
                                                        title="Delete"
                                                        onClick={() => remove(m.id)}
                                                    >×</button>
                                                </div>
                                                {m.description && (
                                                    <p className="milestone-item__description">{m.description}</p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-box" onClick={e => e.stopPropagation()}>
                        <form className="tx-form" onSubmit={e => { e.preventDefault(); if (canSubmit) save(); }}>
                            <h3 className="tx-form__title">{form.id ? 'Edit Milestone' : 'Add Milestone'}</h3>

                            <div className="tx-form__field">
                                <label className="tx-form__label">Title</label>
                                <input
                                    className="tx-form__input"
                                    type="text"
                                    placeholder="e.g. First $10k month"
                                    value={form.title}
                                    onChange={e => setForm(f => ({...f, title: e.target.value}))}
                                    autoFocus
                                    required
                                />
                            </div>

                            <div className="tx-form__field">
                                <label className="tx-form__label">Date</label>
                                <input
                                    className="tx-form__input"
                                    type="date"
                                    value={form.milestoneDate}
                                    onChange={e => setForm(f => ({...f, milestoneDate: e.target.value}))}
                                    required
                                />
                            </div>

                            <div className="tx-form__field">
                                <label className="tx-form__label">
                                    Description <span className="tx-form__optional">(optional)</span>
                                </label>
                                <textarea
                                    className="tx-form__input tx-form__textarea"
                                    placeholder="Notes about this milestone…"
                                    value={form.description}
                                    onChange={e => setForm(f => ({...f, description: e.target.value}))}
                                />
                            </div>

                            <div className="tx-form__actions">
                                <button type="button" className="tx-form__btn" onClick={closeModal}>Cancel</button>
                                <button type="submit" className="tx-form__btn tx-form__btn--primary"
                                        disabled={!canSubmit || saving}>
                                    {saving ? 'Saving…' : form.id ? 'Save' : 'Add'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MilestonesPage;